import React, { useState, useEffect, useRef, useCallback } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { toast } from 'react-toastify'
import { apiLabs, apiSantoLog, apiIDW } from '../../../../Services/api'
import Dashboard from '../../../../Components/Dashboard'
import { store } from '../../../../store'
import history from '../../../../Services/history'
import Denied from '../../../../Components/Denied'
import {
  GlobalAreasStyles, AREA_PALETTE, PageWrap, Topbar, Body,
  Sidebar, SideHeader, DrawBtn, AreaList, NoAreas, Footer,
  ACard, ACardHead, ASwatch, AName, ABtn, AStats, AStat,
  MapContainer, MapDiv, PolyHint, MapCtrl, CtrlBtn, MapStatus,
  ModalOverlay, ModalBox, ModalInput, ModalSelect, ModalActions, ModalBtn,
  ModalLabel, ColorPickerWrap, ColorSwatch, CustomColorRow,
  ConfirmBox, ConfirmIcon, ConfirmTitle, ConfirmMessage, ConfirmActions, ConfirmBtn,
} from './style'
import polygonClipping from 'polygon-clipping'
import {
  COORD_EPS, dedupeAndOpen, coordsToGeom, geomToCoords,
  polygonAreaM2, fmtArea,
  coordsToPCPolygon, pcMultiPolyToCoordsList,
} from '../areaUtils'
import {
  loadGeoCache, mapOrderFromApi, filterValidOrders, geocodeOrdersFromCache, fmtKg,
} from '../orderUtils'

// ─── HELPERS LOCAIS ──────────────────────────────────────────────────────────
function uid() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 6) }

// `apiToLocal` aqui usa AREA_PALETTE como cor padrão (importada do style desta página)
function apiToLocal(a) {
  return {
    id: a.id,
    name: a.name,
    color: a.color || AREA_PALETTE[0],
    coordinates: geomToCoords(a.geom),
    is_active: a.is_active !== false,
    carrier_id: a.carrier_id ?? a.carrierId ?? a.carrier?.id ?? null,
    carrier_name: a.carrier?.name ?? a.carrier_name ?? null,
    driver_id: a.driver_id ?? a.driverId ?? a.driver?.id ?? null,
    driver_name: a.driver?.name ?? a.driver_name ?? null,
  }
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function SantoLogAreas(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  // Draw mode + UI
  const [drawMode, setDrawMode] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [hint, setHint] = useState('')
  const [modal, setModal] = useState({
    open: false, name: '', color: AREA_PALETTE[0],
    carrierId: '', driverId: '', pendingCoords: null, renameId: null,
  })
  const [carriers, setCarriers] = useState([])
  const carriersRef = useRef([])
  useEffect(() => { carriersRef.current = carriers }, [carriers])

  // Pedidos (read-only nesta página, só para visualização durante o desenho de áreas)
  const [orders, setOrders] = useState([])
  const [showOrders, setShowOrders] = useState(true)
  const [ordersLoading, setOrdersLoading] = useState(false)
  const ordersLayerRef = useRef(null)

  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [mapReady, setMapReady] = useState(false)
  const didFitRef = useRef(false)
  const originalAreaRef = useRef(null)  // snapshot da área antes da edição (p/ descartar)
  const [, forceUpdate] = useState(0)
  const rerender = useCallback(() => forceUpdate(n => n + 1), [])

  // Confirmação modal (substitui window.confirm) — abre via askConfirm() e devolve Promise<boolean>
  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: '', message: '', confirmLabel: 'Confirmar', tone: 'warn',
  })
  const confirmResolveRef = useRef(null)

  function askConfirm({ title, message, confirmLabel = 'Confirmar', tone = 'warn' }) {
    return new Promise(resolve => {
      confirmResolveRef.current = resolve
      setConfirmDialog({ open: true, title, message, confirmLabel, tone })
    })
  }
  function closeConfirm(value) {
    setConfirmDialog(d => ({ ...d, open: false }))
    if (confirmResolveRef.current) {
      const r = confirmResolveRef.current
      confirmResolveRef.current = null
      r(Boolean(value))
    }
  }

  // Areas state
  const [areas, setAreas] = useState([])
  const areasRef = useRef([])
  useEffect(() => { areasRef.current = areas }, [areas])

  // Mutable refs read by Leaflet callbacks
  const S = useRef({
    drawMode: false,
    editingId: null,
    polyVerts: [],   // [{lat,lng}] currently being drawn
    mapStatus: 'Pronto',
  })
  const s = S.current
  useEffect(() => { s.drawMode = drawMode }, [drawMode]) // eslint-disable-line
  useEffect(() => { s.editingId = editingId }, [editingId]) // eslint-disable-line

  // Leaflet refs
  const mapDivRef = useRef(null)
  const mapRef = useRef(null)
  const polysLayerRef = useRef(null)
  const polyByIdRef = useRef({})       // { areaId → L.Polygon }
  const drawingPolyRef = useRef(null)  // the in-progress polygon
  const drawingMarkersRef = useRef([]) // vertex previews while drawing
  const editVertexLayerRef = useRef(null)
  const mapContRef = useRef(null)

  // ── INIT MAP ────────────────────────────────────────────────────────────────
  useEffect(() => {
    verifyAuth() // eslint-disable-line
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null } }
  }, []) // eslint-disable-line

  useEffect(() => {
    if (auth !== 'authorized') return
    const rafId = requestAnimationFrame(() => {
      if (!mapDivRef.current || mapRef.current) return
      const map = L.map(mapDivRef.current, { zoomControl: false }).setView([-23.5505, -46.6333], 11)
      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '© OpenStreetMap © CARTO', subdomains: 'abcd', maxZoom: 19,
      }).addTo(map)
      L.control.zoom({ position: 'bottomright' }).addTo(map)
      polysLayerRef.current = L.layerGroup().addTo(map)
      mapRef.current = map
      map.invalidateSize()
      setMapReady(true)
    })
    return () => cancelAnimationFrame(rafId)
  }, [auth])

  // ── API: load areas + carriers + orders ───────────────────────────────────
  useEffect(() => {
    if (auth === 'authorized') {
      fetchCarriers()
      fetchAreas()
      fetchOrders()
    }
  }, [auth]) // eslint-disable-line

  async function fetchAreas() {
    setLoading(true)
    try {
      const res = await apiSantoLog.get('/area')
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.areas || [])
      const mapped = list.map(apiToLocal).filter(a => a.coordinates.length >= 3)
      setAreas(mapped)
    } catch (err) {
      console.error('GET /area failed:', err)
      toast.error('Erro ao carregar áreas: ' + (err?.response?.data?.message || err.message))
    } finally {
      setLoading(false)
    }
  }

  async function fetchCarriers() {
    try {
      const res = await apiSantoLog.get('/carrier')
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.carriers || [])
      setCarriers(list)
    } catch (err) {
      console.error('GET /carrier failed:', err)
      toast.error('Erro ao carregar transportadoras: ' + (err?.response?.data?.message || err.message))
    }
  }

  // Carrega pedidos da mesma fonte de Maps/index.js, mas só geocodifica via cache local
  // (CEPs não cacheados caem no fallback de cidade). Não chama APIs externas para evitar
  // colisão com o mapa de pedidos. Para popular o cache, basta abrir a tela de Mapa primeiro.
  async function fetchOrders() {
    setOrdersLoading(true)
    try {
      const res = await apiIDW.get('/orders', {
        params: { IDStatusOrder: '1,20,22', IDCarrier: '11268,48922, 1905' },
      })
      const raw = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.orders || [])
      const rows = filterValidOrders(raw.map(mapOrderFromApi))
      const cache = loadGeoCache()
      const ready = geocodeOrdersFromCache(rows, cache)
      setOrders(ready)
    } catch (err) {
      console.error('GET /orders failed:', err)
      toast.error('Erro ao carregar pedidos: ' + (err?.response?.data?.message || err.message))
    } finally {
      setOrdersLoading(false)
    }
  }

  // Renderiza/atualiza o layer de markers de pedidos (read-only — só visualização)
  useEffect(() => {
    if (!mapRef.current) return
    if (ordersLayerRef.current) {
      mapRef.current.removeLayer(ordersLayerRef.current)
      ordersLayerRef.current = null
    }
    if (!showOrders || !orders.length) return
    const layer = L.layerGroup().addTo(mapRef.current)
    orders.forEach(o => {
      const m = L.circleMarker([o._lat, o._lng], {
        radius: 3.5,
        color: '#ffd166', weight: 1, opacity: 0.85,
        fillColor: '#ffd166', fillOpacity: 0.7,
        // bubble: cliques no marker passam pro mapa (não atrapalham o desenho do polígono)
        interactive: true, bubblingMouseEvents: true,
      })
      m.bindTooltip(
        `<strong>${String(o['PEDIDO'] || '—')}</strong> · ${fmtKg(o._peso)} kg<br/>` +
        `<span style="color:#8a95a3">${String(o['CIDADE'] || '')}/${String(o['UF'] || '')}</span>`,
        { direction: 'top', offset: [0, -4] }
      )
      m.addTo(layer)
    })
    ordersLayerRef.current = layer
  }, [orders, showOrders, mapReady]) // eslint-disable-line

  // Importante: para POST/PUT *não* parseamos `saved.geom` da resposta. O backend pode
  // ecoar o que enviamos ([lng,lat]) ou devolver no formato do banco ([lat,lng]) e o
  // `detectAxisOrder` não consegue distinguir em coords brasileiras, o que provoca a
  // troca de eixos. Como temos as coordenadas corretas localmente (foi o que mandamos),
  // confiamos nelas e só pegamos id/name/color/is_active da resposta.
  function carrierNameById(carrierId) {
    if (!carrierId) return null
    const c = carriersRef.current.find(x => x.id === carrierId)
    return c?.name ?? null
  }
  function driverNameById(driverId) {
    if (!driverId) return null
    for (const c of carriersRef.current) {
      const d = (c.drivers || []).find(x => x.id === driverId)
      if (d) return d.name ?? null
    }
    return null
  }
  // Motoristas vinculados à transportadora selecionada (lista derivada do array `drivers`)
  function driversFor(carrierId) {
    if (!carrierId) return []
    const c = carriersRef.current.find(x => x.id === carrierId)
    return Array.isArray(c?.drivers) ? c.drivers : []
  }

  async function saveAreaToApi({ name, color, coordinates, carrier_id, driver_id }) {
    const payload = {
      name, color, carrier_id, driver_id: driver_id || null,
      geom: coordsToGeom(coordinates), is_active: true,
    }
    const res = await apiSantoLog.post('/area', payload)
    const saved = res.data?.data || res.data
    return {
      id: saved?.id,
      name: saved?.name ?? name,
      color: saved?.color ?? color,
      coordinates: dedupeAndOpen(coordinates),
      is_active: saved?.is_active ?? true,
      carrier_id: saved?.carrier_id ?? saved?.carrier?.id ?? carrier_id,
      carrier_name: saved?.carrier?.name ?? saved?.carrier_name ?? carrierNameById(carrier_id),
      driver_id: saved?.driver_id ?? saved?.driver?.id ?? driver_id ?? null,
      driver_name: saved?.driver?.name ?? saved?.driver_name ?? driverNameById(driver_id),
    }
  }

  async function updateAreaInApi(id, { name, color, coordinates, carrier_id, driver_id, is_active = true }) {
    const payload = {
      name, color, carrier_id, driver_id: driver_id || null,
      geom: coordsToGeom(coordinates), is_active,
    }
    const res = await apiSantoLog.put(`/area/${id}`, payload)
    const saved = res.data?.data || res.data
    return {
      id: saved?.id ?? id,
      name: saved?.name ?? name,
      color: saved?.color ?? color,
      coordinates: dedupeAndOpen(coordinates),
      is_active: saved?.is_active ?? is_active,
      carrier_id: saved?.carrier_id ?? saved?.carrier?.id ?? carrier_id,
      carrier_name: saved?.carrier?.name ?? saved?.carrier_name ?? carrierNameById(carrier_id),
      driver_id: saved?.driver_id ?? saved?.driver?.id ?? driver_id ?? null,
      driver_name: saved?.driver?.name ?? saved?.driver_name ?? driverNameById(driver_id),
    }
  }

  async function deleteAreaInApi(id) {
    await apiSantoLog.delete(`/area/${id}`)
  }

  // ── Overlap detection / auto-adjust ────────────────────────────────────────
  // Retorna [{ area, intersectionM2, isFullyContained }] para todas as áreas
  // existentes (excluindo a passada em excludeId) que se sobrepõem ao polígono `coordinates`.
  function findOverlappingAreas(coordinates, excludeId = null) {
    const newPoly = coordsToPCPolygon(coordinates)
    if (!newPoly.length) return []
    const out = []
    for (const a of areasRef.current) {
      if (excludeId && a.id === excludeId) continue
      if (!a.coordinates?.length) continue
      const otherPoly = coordsToPCPolygon(a.coordinates)
      if (!otherPoly.length) continue
      let intersection
      try { intersection = polygonClipping.intersection(newPoly, otherPoly) }
      catch (err) { console.error('intersection failed for', a.id, err); continue }
      if (!intersection?.length) continue
      const pieces = pcMultiPolyToCoordsList(intersection)
      const intersectionM2 = pieces.reduce((sum, r) => sum + polygonAreaM2(r), 0)
      if (intersectionM2 < 1) continue // ruído numérico
      const origM2 = polygonAreaM2(a.coordinates)
      const isFullyContained = origM2 > 0 && (intersectionM2 / origM2) > 0.99
      out.push({ area: a, intersectionM2, isFullyContained })
    }
    return out
  }

  // Subtrai `subtractor` de `existing`. Caso o resultado AINDA sobreponha outras
  // áreas (sobreposição pré-existente ou ruído numérico), também subtrai essas —
  // assim a UE final fica guaranteed-livre de sobreposição e o backend aceita o PUT
  // mesmo com validação estrita. Retorna o maior pedaço.
  function subtractAndPickLargest(existingCoords, subtractorCoords, opts = {}) {
    const { excludeId = null } = opts
    const existingPoly = coordsToPCPolygon(existingCoords)
    const subtractorPoly = coordsToPCPolygon(subtractorCoords)
    if (!existingPoly.length) return { coords: existingCoords, pieces: 1 }

    let result = existingPoly
    if (subtractorPoly.length) {
      try { result = polygonClipping.difference(result, subtractorPoly) }
      catch (err) { console.warn('difference (new poly) failed:', err) }
    }

    // Limpa qualquer overlap residual com outras áreas existentes
    if (Array.isArray(result) && result.length) {
      for (const other of areasRef.current) {
        if (excludeId && other.id === excludeId) continue
        const otherPoly = coordsToPCPolygon(other.coordinates)
        if (!otherPoly.length) continue
        try {
          const inter = polygonClipping.intersection(result, otherPoly)
          if (inter && inter.length) {
            const intM2 = pcMultiPolyToCoordsList(inter).reduce((s, r) => s + polygonAreaM2(r), 0)
            if (intM2 > 1) {
              result = polygonClipping.difference(result, otherPoly)
            }
          }
        } catch (err) { console.warn('residual cleanup failed:', err) }
        if (!result?.length) break
      }
    }

    if (!result?.length) return { coords: null, pieces: 0 }
    const pieces = pcMultiPolyToCoordsList(result)
    if (!pieces.length) return { coords: null, pieces: 0 }
    pieces.sort((a, b) => polygonAreaM2(b) - polygonAreaM2(a))
    return { coords: pieces[0], pieces: pieces.length }
  }

  // Pergunta ao usuário se quer ajustar as áreas sobrepostas. Retorna Promise<boolean>.
  function confirmOverlapAdjust(overlaps) {
    const list = overlaps.map(o => {
      const origM2 = polygonAreaM2(o.area.coordinates)
      const pct = origM2 > 0 ? Math.round((o.intersectionM2 / origM2) * 100) : 0
      const tag = o.isFullyContained ? ' [será removida]' : ''
      return `• "${o.area.name}" — ${pct}% sobreposta${tag}`
    }).join('\n')
    const plural = overlaps.length > 1
    return askConfirm({
      title: plural ? 'Áreas sobrepostas detectadas' : 'Área sobreposta detectada',
      message:
        `Esta operação sobrepõe ${overlaps.length} área${plural ? 's' : ''} existente${plural ? 's' : ''}:\n\n` +
        list +
        `\n\nSe confirmar, ${plural ? 'elas serão ajustadas' : 'ela será ajustada'} automaticamente — a porção sobreposta será removida do polígono original.`,
      confirmLabel: 'Confirmar e ajustar',
      tone: 'warn',
    })
  }

  // Aplica os ajustes nos polígonos afetados.
  // Erros do backend propagam pro caller — assim o usuário vê a causa real quando
  // o auto-ajuste falha (ex.: ainda há overlap residual com outra área). Sem isso,
  // a chamada principal de POST/PUT iria falhar depois sem contexto.
  async function applyOverlapAdjustments(overlaps, newCoords) {
    for (const { area, isFullyContained } of overlaps) {
      if (isFullyContained) {
        await deleteAreaInApi(area.id)
        setAreas(prev => prev.filter(a => a.id !== area.id))
        continue
      }
      const result = subtractAndPickLargest(area.coordinates, newCoords, { excludeId: area.id })
      if (!result.coords || result.coords.length < 3) {
        // Sobrou pouco/nada — remove a área
        await deleteAreaInApi(area.id)
        setAreas(prev => prev.filter(a => a.id !== area.id))
        continue
      }
      const updated = await updateAreaInApi(area.id, {
        name: area.name,
        color: area.color,
        carrier_id: area.carrier_id,
        driver_id: area.driver_id,
        coordinates: result.coords,
        is_active: area.is_active,
      })
      setAreas(prev => prev.map(a => a.id === area.id ? updated : a))
    }
  }

  // Re-render polygons when areas change OR map becomes ready OR drawMode toggles
  // (drawMode é dep porque renderAreas troca `interactive` dos polígonos existentes).
  useEffect(() => {
    if (!mapRef.current || !polysLayerRef.current) return
    renderAreas()
    // Auto-fit no primeiro carregamento (apenas uma vez)
    if (!didFitRef.current && areas.length > 0) {
      didFitRef.current = true
      setTimeout(() => fitAllAreas(), 50)
    }
  }, [areas, editingId, mapReady, drawMode]) // eslint-disable-line

  // Keyboard handlers (Escape: cancela desenho ou descarta edição; Enter: finaliza desenho)
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        if (s.drawMode) cancelDraw()
        else if (s.editingId) cancelEdit()
      }
      if (e.key === 'Enter' && s.drawMode && s.polyVerts.length >= 3) {
        finishDraw()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, []) // eslint-disable-line

  // ── RENDER AREAS ────────────────────────────────────────────────────────────
  function renderAreas() {
    if (!polysLayerRef.current) return
    polysLayerRef.current.clearLayers()
    polyByIdRef.current = {}

    areas.forEach(a => {
      const isEditing = a.id === editingId
      // Durante o desenho de novo polígono, polígonos existentes não devem capturar
      // cliques — assim o usuário pode colocar vértice DENTRO de uma área existente.
      const interactive = !drawMode
      const poly = L.polygon(a.coordinates.map(c => [c.lat, c.lng]), {
        color: a.color, weight: 2, fillColor: a.color,
        fillOpacity: isEditing ? 0.05 : (drawMode ? 0.10 : 0.18),
        dashArray: isEditing ? '4,4' : null,
        interactive,
      })
      if (interactive) {
        poly.bindTooltip(`<strong>${escapeHtml(a.name)}</strong>`, { sticky: true, direction: 'top' })
        poly.bindPopup(buildAreaPopup(a), { className: 'area-popup', maxWidth: 240 })
      }
      poly.addTo(polysLayerRef.current)
      polyByIdRef.current[a.id] = poly
    })

    // re-attach edit handles if editing
    if (editingId) attachEditHandles(editingId)
  }

  function buildAreaPopup(a) {
    const m2 = polygonAreaM2(a.coordinates)
    const carrierLabel = a.carrier_name || carrierNameById(a.carrier_id) || '—'
    const driverLabel = a.driver_name || driverNameById(a.driver_id)
    return `<div class="pu">
      <div class="pu-name" style="color:${a.color}">${escapeHtml(a.name)}</div>
      <div class="pu-row"><span class="k">Transportadora</span><span class="v">${escapeHtml(carrierLabel)}</span></div>
      ${driverLabel ? `<div class="pu-row"><span class="k">Motorista</span><span class="v">${escapeHtml(driverLabel)}</span></div>` : ''}
      <div class="pu-row"><span class="k">Vértices</span><span class="v">${a.coordinates.length}</span></div>
      <div class="pu-row"><span class="k">Área</span><span class="v">${fmtArea(m2)}</span></div>
    </div>`
  }
  function escapeHtml(str) {
    return String(str ?? '').replace(/[&<>"']/g, ch =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]))
  }

  // ── DRAW MODE ───────────────────────────────────────────────────────────────
  function startDraw() {
    if (editingId) finishEdit(false)
    setDrawMode(true)
    s.drawMode = true
    s.polyVerts = []
    setHint('Clique no mapa para adicionar pontos · duplo-clique ou Enter para finalizar · ESC cancela')
    if (mapRef.current) {
      mapRef.current.doubleClickZoom.disable()
      mapRef.current.on('click', onMapClickDraw)
      mapRef.current.on('dblclick', onMapDblClickDraw)
    }
  }

  function refreshDrawingPolyline() {
    if (drawingPolyRef.current) { mapRef.current.removeLayer(drawingPolyRef.current); drawingPolyRef.current = null }
    if (s.polyVerts.length >= 2) {
      const latlngs = s.polyVerts.map(v => [v.lat, v.lng])
      drawingPolyRef.current = (s.polyVerts.length >= 3
        ? L.polygon(latlngs, { color: '#00e5a0', weight: 2, dashArray: '6,3', fillOpacity: 0.06 })
        : L.polyline(latlngs, { color: '#00e5a0', weight: 2, dashArray: '6,3' })
      ).addTo(mapRef.current)
    }
  }

  function makeDraggableVertex(ll, isFirst, vertIndex) {
    const handle = L.marker([ll.lat, ll.lng], {
      draggable: true,
      bubblingMouseEvents: false,
      icon: L.divIcon({
        className: '',
        html: `<div class="area-vertex-handle" style="background:${isFirst ? '#00e5a0' : '#fff'}"></div>`,
        iconSize: [12, 12], iconAnchor: [6, 6],
      }),
    }).addTo(mapRef.current)

    handle.on('mousedown', (ev) => { L.DomEvent.stopPropagation(ev) })

    handle.on('drag', (ev) => {
      const p = ev.target.getLatLng()
      const idx = drawingMarkersRef.current.indexOf(ev.target)
      if (idx === -1) return
      s.polyVerts[idx] = { lat: p.lat, lng: p.lng }
      refreshDrawingPolyline()
    })

    if (isFirst) {
      handle.on('click', (ev) => {
        L.DomEvent.stop(ev)
        if (s.polyVerts.length >= 3) finishDraw()
      })
    }
    handle.on('contextmenu', (ev) => {
      L.DomEvent.stop(ev)
      const idx = drawingMarkersRef.current.indexOf(ev.target)
      if (idx === -1) return
      mapRef.current.removeLayer(ev.target)
      drawingMarkersRef.current.splice(idx, 1)
      s.polyVerts.splice(idx, 1)
      refreshDrawingPolyline()
      setHint(s.polyVerts.length < 3
        ? `${s.polyVerts.length} ponto${s.polyVerts.length !== 1 ? 's' : ''} · continue clicando (mínimo 3)`
        : 'Clique no 1° ponto · duplo-clique · ou Enter para finalizar · botão direito remove vértice')
    })
    return handle
  }

  function onMapClickDraw(e) {
    if (!s.drawMode) return
    const ll = e.latlng
    // dedupe: ignora cliques no mesmo ponto (ex: marker propagando p/ mapa, dblclick)
    const last = s.polyVerts[s.polyVerts.length - 1]
    if (last && Math.abs(last.lat - ll.lat) < COORD_EPS && Math.abs(last.lng - ll.lng) < COORD_EPS) return

    s.polyVerts.push({ lat: ll.lat, lng: ll.lng })

    const isFirst = s.polyVerts.length === 1
    const handle = makeDraggableVertex(ll, isFirst, s.polyVerts.length - 1)
    drawingMarkersRef.current.push(handle)

    refreshDrawingPolyline()

    setHint(s.polyVerts.length < 3
      ? `${s.polyVerts.length} ponto${s.polyVerts.length !== 1 ? 's' : ''} · continue clicando (mínimo 3) · arraste pontos para ajustar`
      : 'Clique no 1° ponto · duplo-clique · Enter finaliza · arraste pontos · botão direito remove')
  }
  function onMapDblClickDraw(e) {
    if (!s.drawMode) return
    L.DomEvent.stop(e)
    if (s.polyVerts.length >= 3) finishDraw()
  }

  function finishDraw() {
    const verts = s.polyVerts.slice()
    cleanupDrawingLayers()
    setDrawMode(false)
    s.drawMode = false
    s.polyVerts = []
    setHint('')
    if (mapRef.current) {
      mapRef.current.off('click', onMapClickDraw)
      mapRef.current.off('dblclick', onMapDblClickDraw)
      mapRef.current.doubleClickZoom.enable()
    }
    const nextColor = AREA_PALETTE[areas.length % AREA_PALETTE.length]
    setModal({
      open: true,
      name: `Área ${areas.length + 1}`,
      color: nextColor,
      carrierId: '',
      driverId: '',
      pendingCoords: verts,
      renameId: null,
    })
  }

  function cancelDraw() {
    cleanupDrawingLayers()
    setDrawMode(false)
    s.drawMode = false
    s.polyVerts = []
    setHint('')
    if (mapRef.current) {
      mapRef.current.off('click', onMapClickDraw)
      mapRef.current.off('dblclick', onMapDblClickDraw)
      mapRef.current.doubleClickZoom.enable()
    }
  }

  function cleanupDrawingLayers() {
    if (drawingPolyRef.current) { mapRef.current.removeLayer(drawingPolyRef.current); drawingPolyRef.current = null }
    drawingMarkersRef.current.forEach(m => mapRef.current.removeLayer(m))
    drawingMarkersRef.current = []
  }

  // ── CONFIRM (modal) ─────────────────────────────────────────────────────────
  async function confirmModal() {
    const name = (modal.name || '').trim()
    const color = modal.color || AREA_PALETTE[0]
    const carrier_id = modal.carrierId || null
    const driver_id = modal.driverId || null    // opcional — pode ir como null
    if (!name) return
    if (!carrier_id) {
      toast.warn('Selecione uma transportadora para esta área')
      return
    }

    if (modal.renameId) {
      const id = modal.renameId
      const current = areasRef.current.find(a => a.id === id)
      if (!current) { closeModal(); return }

      // Verifica overlap (ignora a própria área sendo editada)
      const overlaps = findOverlappingAreas(current.coordinates, id)
      if (overlaps.length > 0) {
        const ok = await confirmOverlapAdjust(overlaps)
        if (!ok) return
      }

      setSaving(true)
      let phase = 'init'
      try {
        if (overlaps.length > 0) {
          phase = 'adjust'
          await applyOverlapAdjustments(overlaps, current.coordinates)
        }
        phase = 'update'
        const updated = await updateAreaInApi(id, {
          name,
          color,
          carrier_id,
          driver_id,
          coordinates: current.coordinates,
          is_active: current.is_active,
        })
        setAreas(prev => prev.map(a => a.id === id ? updated : a))
        closeModal()
        toast.success('Área atualizada com sucesso')
      } catch (err) {
        console.error(`[update-area] falhou na fase "${phase}":`, err?.response?.data || err)
        const backendMsg = err?.response?.data?.error || err?.response?.data?.message || err.message
        const prefix = phase === 'adjust'
          ? 'Falha ao ajustar área sobreposta: '
          : 'Erro ao atualizar área: '
        toast.error(prefix + backendMsg)
      } finally {
        setSaving(false)
        if (overlaps.length > 0) fetchAreas() // ressincroniza UI com backend
      }
      return
    }

    // POST de nova área — checa overlap com todas as áreas existentes
    const overlaps = findOverlappingAreas(modal.pendingCoords)
    if (overlaps.length > 0) {
      const ok = await confirmOverlapAdjust(overlaps)
      if (!ok) return
    }

    setSaving(true)
    let phase = 'init'
    try {
      if (overlaps.length > 0) {
        phase = 'adjust'
        await applyOverlapAdjustments(overlaps, modal.pendingCoords)
      }
      phase = 'create'
      const newArea = await saveAreaToApi({
        name, color, carrier_id, driver_id, coordinates: modal.pendingCoords,
      })
      setAreas(prev => [...prev, newArea])
      closeModal()
      toast.success('Área criada com sucesso')
    } catch (err) {
      console.error(`[create-area] falhou na fase "${phase}":`, err?.response?.data || err)
      const backendMsg = err?.response?.data?.error || err?.response?.data?.message || err.message
      const prefix = phase === 'adjust'
        ? 'Falha ao ajustar área sobreposta: '
        : 'Erro ao salvar área: '
      toast.error(prefix + backendMsg)
    } finally {
      setSaving(false)
      if (overlaps.length > 0) fetchAreas()
    }
  }
  function cancelModal() { closeModal() }
  function closeModal() {
    setModal({
      open: false, name: '', color: AREA_PALETTE[0],
      carrierId: '', driverId: '', pendingCoords: null, renameId: null,
    })
  }

  // ── EDIT POLYGON (drag vertices) ────────────────────────────────────────────
  function startEdit(id) {
    if (drawMode) cancelDraw()
    const a = areasRef.current.find(x => x.id === id)
    if (!a) return
    // snapshot — clonagem profunda das coordenadas
    originalAreaRef.current = { ...a, coordinates: a.coordinates.map(c => ({ ...c })) }
    setEditingId(id)
    s.editingId = id
    setHint('Arraste vértices · clique nos pontos tracejados para adicionar · direito remove · Salvar persiste · ESC descarta')
  }

  function attachEditHandles(id) {
    if (editVertexLayerRef.current) {
      mapRef.current.removeLayer(editVertexLayerRef.current)
      editVertexLayerRef.current = null
    }
    const a = areas.find(x => x.id === id); if (!a) return
    const layer = L.layerGroup().addTo(mapRef.current)
    editVertexLayerRef.current = layer
    const n = a.coordinates.length

    a.coordinates.forEach((vert, vIdx) => {
      // Real vertex — draggable + right-click to remove
      const handle = L.marker([vert.lat, vert.lng], {
        draggable: true,
        bubblingMouseEvents: false,
        icon: L.divIcon({
          className: '', html: '<div class="area-vertex-handle"></div>',
          iconSize: [12, 12], iconAnchor: [6, 6],
        }),
      }).addTo(layer)

      handle.on('drag', (e) => {
        const ll = e.target.getLatLng()
        const poly = polyByIdRef.current[id]
        if (!poly) return
        const latlngs = poly.getLatLngs()[0].slice()
        latlngs[vIdx] = ll
        poly.setLatLngs(latlngs)
      })
      handle.on('dragend', (e) => {
        const ll = e.target.getLatLng()
        setAreas(prev => prev.map(x => {
          if (x.id !== id) return x
          const next = x.coordinates.slice()
          next[vIdx] = { lat: ll.lat, lng: ll.lng }
          return { ...x, coordinates: next }
        }))
      })
      handle.on('contextmenu', (e) => {
        L.DomEvent.stop(e)
        setAreas(prev => prev.map(x => {
          if (x.id !== id) return x
          if (x.coordinates.length <= 3) return x
          return { ...x, coordinates: x.coordinates.filter((_, i) => i !== vIdx) }
        }))
      })

      // Ghost midpoint — clicking inserts a new real vertex at this edge
      const next = a.coordinates[(vIdx + 1) % n]
      const midLat = (vert.lat + next.lat) / 2
      const midLng = (vert.lng + next.lng) / 2
      const ghost = L.marker([midLat, midLng], {
        draggable: false,
        bubblingMouseEvents: false,
        icon: L.divIcon({
          className: '', html: '<div class="area-vertex-ghost"></div>',
          iconSize: [12, 12], iconAnchor: [6, 6],
        }),
        zIndexOffset: -100,
      }).addTo(layer)
      ghost.bindTooltip('Clique para adicionar vértice', { direction: 'top', offset: [0, -8] })
      ghost.on('click', (e) => {
        L.DomEvent.stop(e)
        setAreas(prev => prev.map(x => {
          if (x.id !== id) return x
          const arr = x.coordinates.slice()
          arr.splice(vIdx + 1, 0, { lat: midLat, lng: midLng })
          return { ...x, coordinates: arr }
        }))
      })
    })
  }

  function exitEditMode() {
    originalAreaRef.current = null
    setEditingId(null)
    s.editingId = null
    setHint('')
    if (editVertexLayerRef.current && mapRef.current) {
      mapRef.current.removeLayer(editVertexLayerRef.current)
      editVertexLayerRef.current = null
    }
  }

  function cancelEdit() {
    const id = s.editingId
    if (!id) return
    const orig = originalAreaRef.current
    if (orig) {
      setAreas(prev => prev.map(x => x.id === orig.id ? orig : x))
    }
    exitEditMode()
  }

  async function finishEdit() {
    const id = s.editingId
    if (!id) return
    const current = areasRef.current.find(x => x.id === id)
    if (!current) { exitEditMode(); return }

    const ok = await askConfirm({
      title: 'Salvar alterações',
      message: `Confirmar atualização da área "${current.name}"? A geometria atual será gravada no banco.`,
      confirmLabel: 'Salvar',
      tone: 'warn',
    })
    if (!ok) return

    // Verifica se a geometria editada sobrepõe outras áreas (excluindo ela mesma)
    const overlaps = findOverlappingAreas(current.coordinates, id)
    if (overlaps.length > 0) {
      const okAdj = await confirmOverlapAdjust(overlaps)
      if (!okAdj) return
    }

    setSaving(true)
    setHint('Salvando alterações…')
    let phase = 'init'
    try {
      if (overlaps.length > 0) {
        phase = 'adjust'
        await applyOverlapAdjustments(overlaps, current.coordinates)
      }
      phase = 'update'
      const updated = await updateAreaInApi(id, current)
      setAreas(prev => prev.map(x => x.id === id ? updated : x))
      exitEditMode()
      toast.success('Alterações salvas com sucesso')
    } catch (err) {
      console.error(`[finish-edit] falhou na fase "${phase}":`, err?.response?.data || err)
      const backendMsg = err?.response?.data?.error || err?.response?.data?.message || err.message
      const prefix = phase === 'adjust'
        ? 'Falha ao ajustar área sobreposta: '
        : 'Erro ao salvar edição: '
      toast.error(prefix + backendMsg)
      setHint('Erro ao salvar — tente novamente ou descarte')
    } finally {
      setSaving(false)
      if (overlaps.length > 0) fetchAreas()
    }
  }

  // ── DELETE / RENAME / FOCUS ─────────────────────────────────────────────────
  async function deleteArea(id) {
    const a = areasRef.current.find(x => x.id === id)
    const ok = await askConfirm({
      title: 'Remover área',
      message: `Tem certeza que deseja remover "${a?.name || 'esta área'}"? Esta ação não pode ser desfeita.`,
      confirmLabel: 'Remover',
      tone: 'danger',
    })
    if (!ok) return

    if (id === s.editingId) cancelEdit()
    setSaving(true)
    try {
      await deleteAreaInApi(id)
      setAreas(prev => prev.filter(a => a.id !== id))
      toast.success('Área removida')
    } catch (err) {
      console.error('DELETE /area/:id failed:', err)
      toast.error('Erro ao remover área: ' + (err?.response?.data?.error || err.message))
    } finally {
      setSaving(false)
    }
  }
  function renameArea(id) {
    const a = areas.find(x => x.id === id); if (!a) return
    setModal({
      open: true, name: a.name, color: a.color || AREA_PALETTE[0],
      carrierId: a.carrier_id || '',
      driverId: a.driver_id || '',
      pendingCoords: null, renameId: id,
    })
  }
  function focusArea(id) {
    const poly = polyByIdRef.current[id]; if (!poly || !mapRef.current) return
    mapRef.current.fitBounds(poly.getBounds(), { padding: [60, 60] })
    poly.openPopup()
  }

  // ── MAP CONTROLS ────────────────────────────────────────────────────────────
  function fitAllAreas() {
    if (!areas.length || !mapRef.current) return
    const all = areas.flatMap(a => a.coordinates.map(c => [c.lat, c.lng]))
    mapRef.current.fitBounds(L.latLngBounds(all), { padding: [40, 40] })
  }

  function exportJson() {
    const data = JSON.stringify({ areas }, null, 2)
    const blob = new Blob([data], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `areas_${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  // ── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <Dashboard title="Áreas">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <>
          <GlobalAreasStyles />
          <PageWrap>
            <Topbar>
              <h2>📐 Cadastro de Áreas</h2>
              <span className="crumb">Desenhe polígonos para definir zonas de entrega</span>
            </Topbar>

            <Body>
              <Sidebar>
                <SideHeader>
                  <h3>Áreas cadastradas</h3>
                  <p>Desenhe polígonos no mapa para representar zonas de entrega.</p>
                  <DrawBtn $active={drawMode} onClick={drawMode ? cancelDraw : startDraw}>
                    {drawMode ? '✕ Cancelar desenho' : '⬡ Novo polígono'}
                  </DrawBtn>
                </SideHeader>

                <AreaList>
                  {loading && <NoAreas>Carregando áreas…</NoAreas>}
                  {!loading && !areas.length && (
                    <NoAreas>
                      Nenhuma área cadastrada.<br />
                      Clique em <strong>“Novo polígono”</strong> para começar.
                    </NoAreas>
                  )}
                  {areas.map(a => {
                    const isEditing = a.id === editingId
                    const m2 = polygonAreaM2(a.coordinates)
                    return (
                      <ACard key={a.id} $color={a.color} $editing={isEditing}>
                        <ACardHead>
                          <ASwatch $color={a.color} />
                          <AName onClick={() => focusArea(a.id)} title="Centralizar no mapa">{a.name}</AName>
                          {isEditing ? (
                            <>
                              <ABtn onClick={cancelEdit} disabled={saving}>✕ Descartar</ABtn>
                              <ABtn $warn onClick={finishEdit} disabled={saving}>
                                {saving ? '…' : '✓ Salvar'}
                              </ABtn>
                            </>
                          ) : (
                            <ABtn $warn onClick={() => startEdit(a.id)} disabled={saving}>✎ Editar</ABtn>
                          )}
                          <ABtn onClick={() => renameArea(a.id)} disabled={saving} title="Editar dados da área">✏️</ABtn>
                          <ABtn $danger onClick={() => deleteArea(a.id)} disabled={saving} title="Remover área">🗑</ABtn>
                        </ACardHead>
                        <AStats>
                          <AStat><span>{a.coordinates.length}</span> vértices</AStat>
                          <AStat>área <span>{fmtArea(m2)}</span></AStat>
                          {(a.carrier_name || carrierNameById(a.carrier_id)) && (
                            <AStat>🚚 <span>{a.carrier_name || carrierNameById(a.carrier_id)}</span></AStat>
                          )}
                          {(a.driver_name || driverNameById(a.driver_id)) && (
                            <AStat>👤 <span>{a.driver_name || driverNameById(a.driver_id)}</span></AStat>
                          )}
                        </AStats>
                      </ACard>
                    )
                  })}
                </AreaList>

                <Footer>
                  <span>{areas.length} área{areas.length !== 1 ? 's' : ''}</span>
                  <span style={{ display: 'flex', gap: 12 }}>
                    <span style={{ cursor: loading ? 'wait' : 'pointer' }} onClick={() => !loading && fetchAreas()}>
                      {loading ? '…' : '↻ Atualizar'}
                    </span>
                    {areas.length > 0 && (
                      <span style={{ cursor: 'pointer' }} onClick={exportJson}>⤓ JSON</span>
                    )}
                  </span>
                </Footer>
              </Sidebar>

              <MapContainer ref={mapContRef} style={{ cursor: drawMode ? 'crosshair' : 'default' }}>
                <MapDiv ref={mapDivRef} />

                <PolyHint $visible={!!hint}>{hint}</PolyHint>

                <MapCtrl>
                  <CtrlBtn onClick={fitAllAreas} disabled={!areas.length}>👁 Ver todas</CtrlBtn>
                  <CtrlBtn $active={drawMode} onClick={drawMode ? cancelDraw : startDraw}>⬡ {drawMode ? 'Cancelar' : 'Desenhar'}</CtrlBtn>
                  <CtrlBtn
                    $active={showOrders}
                    onClick={() => setShowOrders(v => !v)}
                    title="Mostrar/ocultar pedidos no mapa"
                  >
                    📦 Pedidos {ordersLoading ? '…' : (showOrders ? `${orders.length}` : 'off')}
                  </CtrlBtn>
                </MapCtrl>

                <MapStatus>
                  {drawMode ? `Desenhando · ${s.polyVerts.length} pts` :
                    editingId ? 'Editando' :
                      `${areas.length} área${areas.length !== 1 ? 's' : ''}${showOrders && orders.length ? ` · ${orders.length} pedidos` : ''
                      }`}
                </MapStatus>
              </MapContainer>
            </Body>
          </PageWrap>

          <ModalOverlay $visible={modal.open}>
            <ModalBox>
              <h3>{modal.renameId ? 'Editar área' : 'Nova área'}</h3>
              <p>Defina um nome e uma cor para identificar esta zona no mapa.</p>

              <ModalLabel>Nome</ModalLabel>
              <ModalInput
                autoFocus
                value={modal.name || ''}
                onChange={e => {
                  const v = e.target.value
                  setModal(m => ({ ...m, name: v }))
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !saving) confirmModal()
                  if (e.key === 'Escape') cancelModal()
                }}
                placeholder="Ex: Zona Leste"
              />

              <ModalLabel>Transportadora *</ModalLabel>
              <ModalSelect
                value={modal.carrierId || ''}
                disabled={saving}
                onChange={e => {
                  const v = e.target.value
                  // troca a transportadora reseta o motorista (vínculo era da carrier antiga)
                  setModal(m => ({ ...m, carrierId: v, driverId: '' }))
                }}
              >
                <option value="">— Selecione uma transportadora —</option>
                {carriers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </ModalSelect>

              {modal.carrierId && (() => {
                const list = driversFor(modal.carrierId)
                return (
                  <>
                    <ModalLabel>Motorista <span style={{ textTransform: 'none', opacity: 0.6 }}>(opcional)</span></ModalLabel>
                    <ModalSelect
                      value={modal.driverId || ''}
                      disabled={saving || list.length === 0}
                      onChange={e => {
                        const v = e.target.value
                        setModal(m => ({ ...m, driverId: v }))
                      }}
                    >
                      <option value="">
                        {list.length === 0
                          ? '— Nenhum motorista vinculado a esta transportadora —'
                          : '— Sem motorista (opcional) —'}
                      </option>
                      {list.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.name}{d.document ? ` · ${d.document}` : ''}
                        </option>
                      ))}
                    </ModalSelect>
                  </>
                )
              })()}

              <ModalLabel>Cor</ModalLabel>
              <ColorPickerWrap>
                {AREA_PALETTE.map(hex => (
                  <ColorSwatch
                    key={hex}
                    type="button"
                    title={hex}
                    $color={hex}
                    $selected={modal.color?.toLowerCase() === hex.toLowerCase()}
                    onClick={() => setModal(m => ({ ...m, color: hex }))}
                  />
                ))}
              </ColorPickerWrap>
              <CustomColorRow>
                <span>Cor personalizada:</span>
                <input
                  type="color"
                  value={modal.color || '#00e5a0'}
                  onChange={e => {
                    const v = e.target.value
                    setModal(m => ({ ...m, color: v }))
                  }}
                />
                <span className="hex">{(modal.color || '').toUpperCase()}</span>
              </CustomColorRow>

              <ModalActions>
                <ModalBtn onClick={cancelModal} disabled={saving}>Cancelar</ModalBtn>
                <ModalBtn
                  $primary
                  onClick={confirmModal}
                  disabled={saving || !modal.name?.trim() || !modal.carrierId}
                >
                  {saving ? 'Salvando…' : 'Salvar'}
                </ModalBtn>
              </ModalActions>
            </ModalBox>
          </ModalOverlay>

          {/* CONFIRM MODAL — substitui window.confirm */}
          <ModalOverlay $visible={confirmDialog.open}>
            <ConfirmBox $tone={confirmDialog.tone}>
              <ConfirmIcon $tone={confirmDialog.tone}>
                {confirmDialog.tone === 'danger' ? '⚠' : '?'}
              </ConfirmIcon>
              <ConfirmTitle>{confirmDialog.title}</ConfirmTitle>
              <ConfirmMessage>{confirmDialog.message}</ConfirmMessage>
              <ConfirmActions>
                <ConfirmBtn onClick={() => closeConfirm(false)}>Cancelar</ConfirmBtn>
                <ConfirmBtn $primary $tone={confirmDialog.tone} onClick={() => closeConfirm(true)}>
                  {confirmDialog.confirmLabel}
                </ConfirmBtn>
              </ConfirmActions>
            </ConfirmBox>
          </ModalOverlay>
        </>
      )}
    </Dashboard>
  )
}
